import React, { useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Loader2, Check } from 'lucide-react';
import { FormField } from '../identity/shared/FormField';
import { OAuthProviders, OAuthProvider } from './OAuthProviders';
import { useJWTAuth } from '../../contexts/useJWTAuth';
import type { SignUpData } from '../../contexts/jwtAuthContextInstance';
import { API_CONFIG } from '../../config/api.config';
import { logger } from '../../utils/logger';

interface SignUpFormProps {
  onSuccess: () => void;
  onSwitchToSignIn?: () => void;
}

interface FormData extends SignUpData {
  confirmPassword: string;
  agreeToTerms: boolean;
}

interface ValidationErrors {
  email?: string;
  password?: string;
  confirmPassword?: string;
  firstName?: string;
  agreeToTerms?: string;
}

// Password validation rules
const passwordRules = [
  { test: (pwd: string) => pwd.length >= 8, message: 'At least 8 characters' },
  { test: (pwd: string) => /[A-Z]/.test(pwd), message: 'One uppercase letter' },
  { test: (pwd: string) => /[a-z]/.test(pwd), message: 'One lowercase letter' },
  { test: (pwd: string) => /\d/.test(pwd), message: 'One number' },
  { test: (pwd: string) => /[!@#$%^&*(),.?":{}|<>]/.test(pwd), message: 'One special character' },
];

export const SignUpForm: React.FC<SignUpFormProps> = ({ onSuccess }) => {
  const { signUp, isLoading, error, clearError } = useJWTAuth();
  const navigate = useNavigate();

  // Form state
  const [formData, setFormData] = useState<FormData>({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    agreeToTerms: false,
  });

  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});
  const [showPasswordRules, setShowPasswordRules] = useState(false);
  const [oauthLoading, setOauthLoading] = useState<OAuthProvider | null>(null);

  // Validation functions
  const validateEmail = (email: string): string | undefined => {
    if (!email) return 'Email is required';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) return 'Please enter a valid email address';
    return undefined;
  };

  const validatePassword = (password: string): string | undefined => {
    if (!password) return 'Password is required';
    const failedRules = passwordRules.filter((rule) => !rule.test(password));
    if (failedRules.length > 0) {
      return `Password must meet all requirements`;
    }
    return undefined;
  };

  const validateConfirmPassword = (
    password: string,
    confirmPassword: string,
  ): string | undefined => {
    if (!confirmPassword) return 'Please confirm your password';
    if (password !== confirmPassword) return 'Passwords do not match';
    return undefined;
  };

  const validateFirstName = (firstName: string): string | undefined => {
    if (!firstName.trim()) return 'First name is required';
    if (firstName.trim().length < 2) return 'First name must be at least 2 characters';
    return undefined;
  };

  // Real-time validation
  const validateField = useCallback(
    (field: keyof FormData, value: string | boolean) => {
      let error: string | undefined;

      switch (field) {
        case 'email':
          error = validateEmail(value as string);
          break;
        case 'password':
          error = validatePassword(value as string);
          // Also revalidate confirm password if it exists
          if (formData.confirmPassword) {
            const confirmError = validateConfirmPassword(value as string, formData.confirmPassword);
            setValidationErrors((prev) => ({ ...prev, confirmPassword: confirmError }));
          }
          break;
        case 'confirmPassword':
          error = validateConfirmPassword(formData.password, value as string);
          break;
        case 'firstName':
          error = validateFirstName(value as string);
          break;
        case 'agreeToTerms':
          error = !value ? 'You must agree to the terms of service' : undefined;
          break;
      }

      setValidationErrors((prev) => ({ ...prev, [field]: error }));
      return error;
    },
    [formData.password, formData.confirmPassword],
  );

  // Handle input changes
  const handleInputChange = (field: keyof FormData, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    // Clear auth errors when user starts typing
    if (error) {
      clearError();
    }

    // Validate field on change (debounced for better UX)
    if (typeof value === 'string' && value.length > 0) {
      setTimeout(() => validateField(field, value), 300);
    } else if (field === 'agreeToTerms') {
      validateField(field, value);
    }
  };

  // Validate entire form
  const validateForm = (): boolean => {
    const errors: ValidationErrors = {
      email: validateEmail(formData.email),
      password: validatePassword(formData.password),
      confirmPassword: validateConfirmPassword(formData.password, formData.confirmPassword),
      firstName: validateFirstName(formData.firstName),
      agreeToTerms: !formData.agreeToTerms ? 'You must agree to the terms of service' : undefined,
    };

    setValidationErrors(errors);
    return !Object.values(errors).some((error) => error !== undefined);
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      const signUpData: SignUpData = {
        email: formData.email.trim(),
        password: formData.password,
        firstName: formData.firstName.trim(),
      };

      // Pass onSuccess as the callback - it will be called after user state is set
      const result = await signUp(signUpData, () => {
        // Navigate to dashboard - called after auth state is fully propagated
        onSuccess();
      });

      // Check if email verification is required
      if (result.requiresVerification) {
        // Redirect to email verification required page
        navigate('/verify-email');
        return;
      }
    } catch (error) {
      // Error is handled by the auth context
      logger.error('Sign up failed', { error });
    }
  };

  // Handle OAuth sign up
  const handleOAuthSignUp = async (provider: OAuthProvider) => {
    setOauthLoading(provider);
    try {
      const response = await fetch(`${API_CONFIG.apiUrl}/auth/oauth/${provider}/initiate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Failed to initiate ${provider} OAuth`);
      }

      const { authorizationUrl } = await response.json();

      // Redirect to the provider's OAuth authorization page
      window.location.href = authorizationUrl;
    } catch (error) {
      logger.error(`${provider} sign up failed`, { error });
      alert(error instanceof Error ? error.message : `Failed to sign up with ${provider}`);
      setOauthLoading(null);
    }
  };

  const getPasswordStrength = (password: string) => {
    const passedRules = passwordRules.filter((rule) => rule.test(password));
    return {
      score: passedRules.length,
      total: passwordRules.length,
      percentage: (passedRules.length / passwordRules.length) * 100,
    };
  };

  const passwordStrength = getPasswordStrength(formData.password);

  return (
    <div className="space-y-6">
      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* First Name */}
        <FormField
          label="First Name"
          type="text"
          value={formData.firstName}
          onChange={(value) => handleInputChange('firstName', value)}
          error={validationErrors.firstName}
          placeholder="Enter your first name"
          autoComplete="given-name"
          disabled={isLoading}
        />

        {/* Email */}
        <FormField
          label="Email"
          type="email"
          value={formData.email}
          onChange={(value) => handleInputChange('email', value)}
          error={validationErrors.email}
          placeholder="Enter your email address"
          autoComplete="email"
          disabled={isLoading}
        />

        {/* Password */}
        <div className="space-y-2">
          <FormField
            label="Password"
            type="password"
            value={formData.password}
            onChange={(value) => handleInputChange('password', value)}
            error={validationErrors.password}
            placeholder="Create a strong password"
            autoComplete="new-password"
            disabled={isLoading}
            onFocus={() => setShowPasswordRules(true)}
          />

          {/* Password Strength Indicator */}
          {formData.password && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <div className="flex-1 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className="h-full transition-all duration-300 rounded-full"
                    style={{
                      width: `${passwordStrength.percentage}%`,
                      backgroundColor:
                        passwordStrength.percentage < 40
                          ? '#ef4444'
                          : passwordStrength.percentage < 80
                            ? '#f59e0b'
                            : '#10b981',
                    }}
                  />
                </div>
                <span
                  className="text-xs font-medium"
                  style={{ color: 'var(--color-text-tertiary)' }}
                >
                  {passwordStrength.score}/{passwordStrength.total}
                </span>
              </div>

              {/* Password Rules */}
              {showPasswordRules && (
                <div className="grid grid-cols-1 gap-1 text-xs">
                  {passwordRules.map((rule, index) => {
                    const passed = rule.test(formData.password);
                    return (
                      <div key={index} className="flex items-center gap-2">
                        <Check
                          className={`w-3 h-3 ${passed ? 'text-green-500' : 'text-gray-400'}`}
                        />
                        <span
                          className={
                            passed ? 'text-green-600 dark:text-green-400' : 'text-gray-500'
                          }
                        >
                          {rule.message}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Confirm Password */}
        <FormField
          label="Confirm Password"
          type="password"
          value={formData.confirmPassword}
          onChange={(value) => handleInputChange('confirmPassword', value)}
          error={validationErrors.confirmPassword}
          placeholder="Confirm your password"
          autoComplete="new-password"
          disabled={isLoading}
        />

        {/* Terms Agreement */}
        <div className="space-y-2">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.agreeToTerms}
              onChange={(e) => handleInputChange('agreeToTerms', e.target.checked)}
              disabled={isLoading}
              className="mt-1 w-4 h-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500 focus:ring-offset-0"
              style={{
                accentColor: 'var(--color-accent-teal)',
              }}
            />
            <span
              className="text-sm leading-relaxed"
              style={{ color: 'var(--color-text-secondary)' }}
            >
              I agree to the{' '}
              <Link
                to="https://autar.ai/terms"
                className="underline hover:no-underline transition-all duration-200"
                style={{ color: 'var(--color-accent-teal)' }}
              >
                Terms of Service
              </Link>{' '}
              and{' '}
              <a
                href="https://autar.ai/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:no-underline transition-all duration-200"
                style={{ color: 'var(--color-accent-teal)' }}
              >
                Privacy Policy
              </a>
            </span>
          </label>
          {validationErrors.agreeToTerms && (
            <p className="text-sm text-red-600 flex items-center gap-2">
              <span>{validationErrors.agreeToTerms}</span>
            </p>
          )}
        </div>

        {/* Auth Error Display */}
        {error && (
          <div className="p-3 rounded-lg border border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20">
            <p className="text-sm text-red-600 dark:text-red-400">{error.message}</p>
          </div>
        )}

        {/* Submit Button */}
        <button
          type="submit"
          disabled={isLoading || !formData.agreeToTerms}
          className="btn-primary w-full flex items-center justify-center gap-2 py-3 text-base font-medium"
        >
          {isLoading ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Creating Account...
            </>
          ) : (
            'Create Account'
          )}
        </button>
      </form>

      {/* OAuth Providers */}
      <OAuthProviders
        onProvider={handleOAuthSignUp}
        isLoading={!!oauthLoading}
        loadingProvider={oauthLoading}
        disabled={isLoading}
      />

      {/* Sign In Link */}
      <div className="text-center">
        <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
          Already have an account?{' '}
          <Link
            to="/signin"
            className="font-medium underline hover:no-underline transition-all duration-200"
            style={{ color: 'var(--color-accent-teal)' }}
          >
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
};
